import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../UI/Button';
import { Modal } from '../UI/Modal';
import { MilestoneEditor } from './MilestoneEditor';
import { CreateContractInput, CreateMilestoneInput } from '../../types/contract';
import {
    ArrowLeft,
    ArrowRight,
    Check,
    CheckCircle,
    FileText,
    Maximize2,
    Minimize2,
    Save,
    Upload,
    X
} from 'lucide-react';
import { useToast } from '../../components/UI/Toast';
import { contractService } from '../../services/contractService';

interface ContractFormProps {
    initialData?: Partial<CreateContractInput>;
    onSubmit: (data: CreateContractInput, isDraft?: boolean) => void;
    onCancel: () => void;
    isSubmitting?: boolean;
}

type ContractSource = 'platform' | 'custom';
type ExpandableField = 'requirements' | 'terms';

const stepLabels = [
    { step: 1, title: '基本信息' },
    { step: 2, title: '合同条款' },
    { step: 3, title: '里程碑' },
    { step: 4, title: '预览提交' }
];

const defaultDeliveryDate = () => Date.now() + 30 * 24 * 60 * 60 * 1000;

const stripPdfSuffix = (fileName: string) => fileName.replace(/\.pdf$/i, '').trim();

export const ContractForm: React.FC<ContractFormProps> = ({
    initialData,
    onSubmit,
    onCancel,
    isSubmitting = false
}) => {
    const { showToast } = useToast();
    const isClientLocked = !!initialData?.client_user_id;
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [currentStep, setCurrentStep] = useState(1);
    const [contractSource, setContractSource] = useState<ContractSource>(
        initialData?.custom_pdf_oss_key ? 'custom' : 'platform'
    );
    const [formData, setFormData] = useState<CreateContractInput>({
        client_user_id: '',
        title: '',
        description: '',
        requirements: '',
        terms: '',
        total_amount: 0,
        currency: 'CNY',
        estimated_delivery_date: defaultDeliveryDate(),
        milestones: [],
        ...initialData
    });
    const [uploadedPdf, setUploadedPdf] = useState<{ ossKey: string; fileName: string; size: number } | null>(
        initialData?.custom_pdf_oss_key
            ? { 
                ossKey: initialData.custom_pdf_oss_key, 
                fileName: initialData.title ? `${initialData.title}.pdf` : '已上传合同文件.pdf', 
                size: 0 
              }
            : null
    );
    const [uploading, setUploading] = useState(false);
    const [previewingPdf, setPreviewingPdf] = useState(false);
    const [pdfPreview, setPdfPreview] = useState<{ url: string; fileName: string; objectUrl?: boolean } | null>(null);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [expandedField, setExpandedField] = useState<ExpandableField | null>(null);

    const isCustomContract = contractSource === 'custom';
    const hasCustomPdf = isCustomContract && !!uploadedPdf;
    const termsLabel = isCustomContract ? '补充条款' : '合同条款';
    const longTextareaClass = 'w-full px-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 resize-y leading-6';

    useEffect(() => {
        if (!initialData) return;
        setFormData(prev => ({ ...prev, ...initialData }));
        if (initialData.custom_pdf_oss_key) {
            setContractSource('custom');
            setUploadedPdf(prev => prev || {
                ossKey: initialData.custom_pdf_oss_key || '',
                fileName: initialData.title ? `${initialData.title}.pdf` : '已上传合同文件.pdf',
                size: 0
            });
        }
    }, [initialData]);

    useEffect(() => {
        return () => {
            if (pdfPreview?.objectUrl) {
                URL.revokeObjectURL(pdfPreview.url);
            }
        };
    }, [pdfPreview]);

    const updateFormData = <K extends keyof CreateContractInput>(key: K, value: CreateContractInput[K]) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.type !== 'application/pdf') {
            showToast('只支持 PDF 文件', 'error');
            return;
        }
        if (file.size > 20 * 1024 * 1024) {
            showToast('PDF 文件不能超过 20MB', 'error');
            return;
        }

        setUploading(true);
        try {
            const res = await contractService.uploadCustomPdf(file);
            const data = (res.data as any)?.data || res.data;
            if (data?.ossKey) {
                const fileTitle = stripPdfSuffix(data.fileName || file.name);
                setUploadedPdf({
                    ossKey: data.ossKey,
                    fileName: data.fileName || file.name,
                    size: data.size || file.size
                });
                setFormData(prev => ({
                    ...prev,
                    custom_pdf_oss_key: data.ossKey,
                    title: prev.title || fileTitle || '自定义合同'
                }));
                setContractSource('custom');
                showToast('PDF 上传成功', 'success');
            } else {
                showToast('上传失败', 'error');
            }
        } catch (err: any) {
            showToast(err?.response?.data?.error || '上传失败', 'error');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleRemovePdf = () => {
        setUploadedPdf(null);
        setPdfPreview(null);
        setAgreedToTerms(false);
        setFormData(prev => ({ ...prev, custom_pdf_oss_key: undefined }));
    };

    const handlePreviewUploadedPdf = async () => {
        if (!uploadedPdf?.ossKey) return;

        setPreviewingPdf(true);
        try {
            const res = await contractService.getCustomPdfPreviewUrl(uploadedPdf.ossKey, uploadedPdf.fileName);
            const responseData = (res.data as any)?.data || res.data;

            if (res.success && responseData?.previewUrl) {
                setPdfPreview({
                    url: responseData.previewUrl,
                    fileName: uploadedPdf.fileName || '合同文件.pdf',
                    objectUrl: !!responseData.isObjectUrl,
                });
            } else {
                showToast(res.error || '无法预览合同文件', 'error');
            }
        } catch (err: any) {
            showToast(err?.response?.data?.error || '无法预览合同文件，请稍后重试', 'error');
        } finally {
            setPreviewingPdf(false);
        }
    };

    const handleMilestonesChange = (newMilestones: CreateMilestoneInput[]) => {
        const total = newMilestones.reduce((sum, m) => sum + (Number(m.payment_amount) || 0), 0);
        const lastDate = newMilestones.length > 0
            ? Math.max(...newMilestones.map(m => m.estimated_completion_date || Date.now()))
            : defaultDeliveryDate();

        setFormData(prev => ({
            ...prev,
            milestones: newMilestones,
            total_amount: total,
            estimated_delivery_date: lastDate
        }));
    };

    const normalizedMilestones = (isDraft: boolean) => {
        if (!isDraft) return formData.milestones;
        return formData.milestones.filter(m =>
            m.title.trim() || m.description.trim() || Number(m.payment_amount) > 0
        );
    };

    const buildPayload = (isDraft: boolean): CreateContractInput => {
        const usingCustomPdf = contractSource === 'custom' && !!uploadedPdf;
        const title = formData.title.trim()
            || (usingCustomPdf ? stripPdfSuffix(uploadedPdf?.fileName || '') : '')
            || (isDraft ? '未命名合同草稿' : '合作合同');

        return {
            ...formData,
            client_user_id: formData.client_user_id.trim(),
            brief_id: formData.brief_id?.trim() || undefined,
            title,
            description: usingCustomPdf ? (formData.description || '详见上传合同文件') : formData.description.trim(),
            requirements: usingCustomPdf ? (formData.requirements || '详见上传合同文件') : formData.requirements.trim(),
            terms: formData.terms.trim(),
            currency: formData.currency || 'CNY',
            total_amount: formData.total_amount || 0,
            estimated_delivery_date: formData.estimated_delivery_date || defaultDeliveryDate(),
            milestones: normalizedMilestones(isDraft),
            custom_pdf_oss_key: usingCustomPdf ? uploadedPdf?.ossKey : undefined
        };
    };

    const validateStep = (step: number, forSubmit = false) => {
        if (step === 1) {
            if (!formData.client_user_id.trim()) {
                showToast('请填写客户 ID', 'error');
                return false;
            }
            if (contractSource === 'custom' && !uploadedPdf) {
                showToast('请选择并上传合同 PDF，或切换为新建合同', 'error');
                return false;
            }
            if (uploading) {
                showToast('合同文件仍在上传中，请稍后', 'error');
                return false;
            }
            if (contractSource === 'platform' && (!formData.title.trim() || !formData.description.trim() || !formData.requirements.trim())) {
                showToast('请填写合同标题、项目描述和项目要求', 'error');
                return false;
            }
        }

        if (step === 2) {
            if (!formData.terms.trim()) {
                showToast(hasCustomPdf ? '请填写补充条款' : '请填写合同条款', 'error');
                return false;
            }
            if (isCustomContract && forSubmit && !agreedToTerms) {
                showToast('请确认上传合同内容合法，并同意平台基础条款', 'error');
                return false;
            }
        }

        if (step === 3 || (step === 4 && forSubmit)) {
            if (formData.milestones.length === 0) {
                showToast('请至少添加一个里程碑', 'error');
                return false;
            }
            const invalidMilestone = formData.milestones.some(m =>
                !m.title.trim()
                || !m.description.trim()
                || !m.estimated_completion_date
                || Number(m.payment_amount) <= 0
                || m.requires_deliverable === undefined
            );
            if (invalidMilestone) {
                showToast('请补全每个里程碑的标题、描述、截止日期和付款金额', 'error');
                return false;
            }
            if (!formData.milestones.some(m => m.requires_deliverable)) {
                showToast('合同中必须至少包含一个需要交付物的里程碑', 'error');
                return false;
            }
        }

        return true;
    };

    const validateSubmit = () => {
        return stepLabels.every(item => validateStep(item.step, true));
    };

    const handleSaveDraft = () => {
        if (!formData.client_user_id.trim()) {
            showToast('保存草稿前请先填写客户 ID', 'error');
            return;
        }
        onSubmit(buildPayload(true), true);
    };

    const handleFinalSubmit = () => {
        if (!validateSubmit()) return;
        onSubmit(buildPayload(false), false);
    };

    const goNext = () => {
        setCurrentStep(prev => Math.min(prev + 1, stepLabels.length));
    };

    const goPrev = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
    };

    const renderExpandableTextarea = (
        field: ExpandableField,
        label: string,
        placeholder: string,
        rows: number
    ) => (
        <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
                {label} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
                <textarea
                    value={formData[field]}
                    onChange={(e) => updateFormData(field, e.target.value)}
                    rows={rows}
                    className={`${longTextareaClass} min-h-[132px] pr-12`}
                    placeholder={placeholder}
                />
                <button
                    type="button"
                    onClick={() => setExpandedField(field)}
                    className="absolute top-2 right-2 inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:text-slate-700 transition-colors"
                    aria-label={`全屏编辑${label}`}
                    title={`全屏编辑${label}`}
                >
                    <Maximize2 className="h-4 w-4" />
                </button>
            </div>
        </div>
    );

    const expandedEditor = expandedField ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
            <div
                className="flex h-[86vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
                role="dialog"
                aria-modal="true"
                aria-label={`全屏编辑${expandedField === 'terms' ? termsLabel : '项目要求'}`}
            >
                <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                    <h3 className="text-base font-semibold text-slate-900">
                        {expandedField === 'terms' ? termsLabel : '项目要求'}
                    </h3>
                    <button
                        type="button"
                        onClick={() => setExpandedField(null)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        aria-label="退出全屏编辑"
                        title="退出全屏编辑"
                    >
                        <Minimize2 className="h-4 w-4" />
                    </button>
                </div>
                <textarea
                    autoFocus
                    value={formData[expandedField]}
                    onChange={(e) => updateFormData(expandedField, e.target.value)}
                    className="min-h-0 flex-1 resize-none border-0 px-5 py-4 text-sm leading-7 text-slate-800 outline-none focus:ring-0"
                    placeholder={expandedField === 'terms' ? (isCustomContract ? '输入需要附加到上传合同后的补充条款' : '版权归属、修改次数等条款...') : '详细的项目要求列表...'}
                />
            </div>
        </div>
    ) : null;

    const renderStepIndicator = () => (
        <div className="border-b border-slate-100 pb-5">
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                {stepLabels.map(item => {
                    const isActive = item.step === currentStep;
                    const isDone = item.step < currentStep;

                    return (
                        <button
                            key={item.step}
                            type="button"
                            onClick={() => setCurrentStep(item.step)}
                            className={`min-w-0 rounded-xl border px-2 py-3 text-left transition-colors ${isActive
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : isDone
                                    ? 'border-slate-200 bg-slate-50 text-slate-700'
                                    : 'border-slate-200 bg-white text-slate-500'
                                }`}
                        >
                            <div className="flex items-center gap-2">
                                <span className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${isActive
                                    ? 'bg-blue-600 text-white'
                                    : isDone
                                        ? 'bg-slate-700 text-white'
                                        : 'bg-slate-100 text-slate-500'
                                    }`}>
                                    {isDone ? <Check className="h-3.5 w-3.5" /> : item.step}
                                </span>
                                <span className="truncate text-sm font-medium">{item.title}</span>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );

    const renderStepOne = () => (
        <section className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-slate-900">基本信息</h3>
                <p className="mt-1 text-sm text-slate-500">先确认客户身份，并选择新建合同或上传已有合同 PDF。</p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                        客户 ID <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={formData.client_user_id}
                        onChange={(e) => updateFormData('client_user_id', e.target.value)}
                        disabled={isClientLocked}
                        className={`w-full px-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 ${isClientLocked ? 'bg-slate-100 text-slate-500' : ''}`}
                        placeholder="请输入客户的用户 ID"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Brief ID (可选)</label>
                    <input
                        type="text"
                        value={formData.brief_id || ''}
                        onChange={(e) => updateFormData('brief_id', e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                        placeholder="请输入关联的需求 ID"
                    />
                </div>
            </div>

            <div className="space-y-3">
                <p className="text-sm font-medium text-slate-700">合同方式</p>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <label className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${contractSource === 'platform'
                        ? 'border-blue-500 bg-blue-50 text-blue-800'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                        }`}>
                        <input
                            type="radio"
                            name="contractSource"
                            checked={contractSource === 'platform'}
                            onChange={() => setContractSource('platform')}
                            className="mt-1 border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>
                            <span className="block text-sm font-semibold">新建合同</span>
                            <span className="mt-1 block text-xs text-slate-500">填写项目内容，由平台生成合同 PDF。</span>
                        </span>
                    </label>
                    <label className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${contractSource === 'custom'
                        ? 'border-blue-500 bg-blue-50 text-blue-800'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                        }`}>
                        <input
                            type="radio"
                            name="contractSource"
                            checked={contractSource === 'custom'}
                            onChange={() => setContractSource('custom')}
                            className="mt-1 border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>
                            <span className="block text-sm font-semibold">上传合同 PDF</span>
                            <span className="mt-1 block text-xs text-slate-500">已有正式合同文件时使用。</span>
                        </span>
                    </label>
                </div>
            </div>

            {contractSource === 'platform' && (
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            合同标题 <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => updateFormData('title', e.target.value)}
                            className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                            placeholder="例如：品牌宣传视频制作服务协议"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            项目描述 <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => updateFormData('description', e.target.value)}
                            rows={3}
                            className={`${longTextareaClass} min-h-[112px]`}
                            placeholder="简要描述项目的目标和范围"
                        />
                    </div>

                    {renderExpandableTextarea('requirements', '项目要求', '详细的项目要求列表...', 4)}
                </div>
            )}

            {contractSource === 'custom' && (
                <div className="space-y-3">
                    {!uploadedPdf ? (
                        <div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="application/pdf"
                                onChange={handlePdfUpload}
                                className="hidden"
                            />
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                className="w-full border-2 border-dashed border-slate-300 rounded-xl p-6 hover:border-blue-400 hover:bg-blue-50/30 transition-colors flex flex-col items-center gap-2 disabled:opacity-50"
                            >
                                <Upload className="w-6 h-6 text-slate-400" />
                                <span className="text-sm text-slate-600">
                                    {uploading ? '上传中...' : '点击上传合同 PDF（最大 20MB）'}
                                </span>
                            </button>
                        </div>
                    ) : (
                        <div 
                            className={`flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 group transition-colors ${previewingPdf ? 'opacity-50 cursor-wait' : 'cursor-pointer hover:border-blue-400 hover:bg-blue-50/50'}`}
                            onClick={() => !previewingPdf && handlePreviewUploadedPdf()}
                        >
                            <FileText className="h-5 w-5 shrink-0 text-blue-500" />
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-slate-900 group-hover:text-blue-600 transition-colors">
                                    {previewingPdf ? '正在加载预览...' : uploadedPdf.fileName}
                                </p>
                                <p className="text-xs text-slate-500">
                                    {uploadedPdf.size ? `${(uploadedPdf.size / 1024).toFixed(1)} KB` : '已上传'}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleRemovePdf(); }}
                                className="text-slate-400 hover:text-red-500 p-1"
                                aria-label="移除合同文件"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                </div>
            )}
        </section>
    );

    const renderStepTwo = () => (
        <section className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-slate-900">{termsLabel}</h3>
                <p className="mt-1 text-sm text-slate-500">
                    {isCustomContract
                        ? '补充条款会附加在上传合同之后，用于补充平台侧或本次合作的特殊约定。'
                        : '合同条款用于约定版权归属、修改次数、验收和其他商业条件。'}
                </p>
            </div>

            {renderExpandableTextarea(
                'terms',
                termsLabel,
                isCustomContract ? '输入需要附加到上传合同后的补充条款' : '版权归属、修改次数等条款...',
                5
            )}

            {isCustomContract && (
                <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <input
                        type="checkbox"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                        className="mt-0.5 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                    />
                    <span className="text-sm leading-6 text-amber-900">
                        我确认上传的合同内容真实、合法、合规，并同意平台基础条款会附加到签署文件中。
                    </span>
                </label>
            )}
        </section>
    );

    const renderStepThree = () => (
        <section className="space-y-6">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <div>
                    <h3 className="text-lg font-semibold text-slate-900">项目里程碑</h3>
                    <p className="mt-1 text-sm text-slate-500">设置付款节点、交付物要求和预计完成时间。</p>
                </div>
                <div className="text-right">
                    <p className="text-xs text-slate-500">总金额</p>
                    <p className="text-xl font-bold text-slate-900">
                        {formData.currency === 'CNY' ? '¥' : '$'}
                        {formData.total_amount.toLocaleString()}
                    </p>
                </div>
            </div>

            <MilestoneEditor
                milestones={formData.milestones}
                onChange={handleMilestonesChange}
                currency={formData.currency}
            />

            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                <p className="text-sm font-medium text-slate-900">客户审核期限</p>
                <p className="mt-1 text-xs text-slate-500">里程碑提交后，客户在此期限内必须完成审核或修改回复，否则系统将自动通过并释放款项。</p>
                <div className="mt-3 flex gap-2">
                    {[72, 120, 168].map((h) => {
                        const active = (formData.review_sla_hours ?? 120) === h;
                        return (
                            <button
                                key={h}
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, review_sla_hours: h }))}
                                className={`px-4 py-1.5 text-sm rounded-full border transition-colors ${
                                    active
                                        ? 'bg-black text-white border-black'
                                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                                }`}
                            >
                                {h / 24} 天
                            </button>
                        );
                    })}
                </div>
            </div>
        </section>
    );

    const renderPreviewRow = (label: string, value?: React.ReactNode) => (
        <div className="border-b border-slate-100 py-3 last:border-0">
            <p className="text-xs font-medium text-slate-500">{label}</p>
            <div className="mt-1 text-sm leading-6 text-slate-900">{value || <span className="text-slate-400">未填写</span>}</div>
        </div>
    );

    const renderStepFour = () => {
        const payload = buildPayload(false);
        return (
            <section className="space-y-6">
                <div>
                    <h3 className="text-lg font-semibold text-slate-900">预览合同</h3>
                    <p className="mt-1 text-sm text-slate-500">提交后将发送给客户确认并进入签署流程。</p>
                </div>

                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <div className="rounded-xl border border-slate-200 p-4">
                        <h4 className="mb-2 text-sm font-semibold text-slate-900">基础信息</h4>
                        {renderPreviewRow('客户 ID', payload.client_user_id)}
                        {renderPreviewRow('Brief ID', payload.brief_id)}
                        {renderPreviewRow('合同来源', hasCustomPdf ? (
                            <button
                                type="button"
                                onClick={handlePreviewUploadedPdf}
                                disabled={previewingPdf}
                                className="inline-flex max-w-full items-center gap-1.5 rounded-md text-left text-blue-600 transition-colors hover:text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-wait disabled:text-slate-400"
                            >
                                <FileText className="h-4 w-4 shrink-0" />
                                <span className="truncate">
                                    {previewingPdf ? '正在加载合同文件...' : `上传文件：${uploadedPdf?.fileName || '合同文件.pdf'}`}
                                </span>
                                <Maximize2 className="h-3.5 w-3.5 shrink-0" />
                            </button>
                        ) : '新建合同')}
                        {renderPreviewRow('合同标题', payload.title)}
                    </div>

                    <div className="rounded-xl border border-slate-200 p-4">
                        <h4 className="mb-2 text-sm font-semibold text-slate-900">金额与周期</h4>
                        {renderPreviewRow('总金额', `${payload.currency === 'CNY' ? '¥' : '$'}${payload.total_amount.toLocaleString()}`)}
                        {renderPreviewRow('预计交付日期', new Date(payload.estimated_delivery_date).toLocaleDateString('zh-CN'))}
                        {renderPreviewRow('客户审核期限', `${(payload.review_sla_hours ?? 120) / 24} 天`)}
                        {renderPreviewRow('里程碑数量', `${payload.milestones.length} 个`)}
                    </div>
                </div>

                {!hasCustomPdf && (
                    <div className="rounded-xl border border-slate-200 p-4">
                        <h4 className="mb-2 text-sm font-semibold text-slate-900">项目内容</h4>
                        {renderPreviewRow('项目描述', <p className="whitespace-pre-line">{payload.description}</p>)}
                        {renderPreviewRow('项目要求', <p className="whitespace-pre-line">{payload.requirements}</p>)}
                    </div>
                )}

                <div className="rounded-xl border border-slate-200 p-4">
                    <h4 className="mb-2 text-sm font-semibold text-slate-900">{termsLabel}</h4>
                    <p className="whitespace-pre-line text-sm leading-6 text-slate-800">{payload.terms || '未填写'}</p>
                </div>

                <div className="rounded-xl border border-slate-200 p-4">
                    <h4 className="mb-3 text-sm font-semibold text-slate-900">里程碑</h4>
                    <div className="space-y-3">
                        {payload.milestones.length === 0 ? (
                            <p className="text-sm text-slate-400">还没有设置里程碑</p>
                        ) : payload.milestones.map((milestone, index) => (
                            <div key={`${milestone.title}-${index}`} className="rounded-lg bg-slate-50 p-3">
                                <div className="flex items-center justify-between gap-3">
                                    <p className="text-sm font-medium text-slate-900">{index + 1}. {milestone.title || '未命名里程碑'}</p>
                                    <p className="shrink-0 text-sm font-semibold text-slate-900">
                                        {payload.currency === 'CNY' ? '¥' : '$'}{Number(milestone.payment_amount || 0).toLocaleString()}
                                    </p>
                                </div>
                                <p className="mt-1 text-sm leading-6 text-slate-600">{milestone.description || '未填写描述'}</p>
                                <p className="mt-1 text-xs text-slate-500">
                                    截止日期：{new Date(milestone.estimated_completion_date).toLocaleDateString('zh-CN')}
                                    {milestone.requires_deliverable ? ' · 需要交付物' : ' · 不需要交付物'}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    };

    const renderCurrentStep = () => {
        if (currentStep === 1) return renderStepOne();
        if (currentStep === 2) return renderStepTwo();
        if (currentStep === 3) return renderStepThree();
        return renderStepFour();
    };

    return (
        <div className="space-y-6">
            {expandedEditor && createPortal(expandedEditor, document.body)}
            <Modal
                isOpen={!!pdfPreview}
                onClose={() => setPdfPreview(null)}
                title={pdfPreview?.fileName || '合同文件预览'}
                size="xl"
                zIndex={80}
            >
                <div className="h-[74vh] min-h-[460px] overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
                    {pdfPreview && (
                        <iframe
                            src={`${pdfPreview.url}#toolbar=1&navpanes=0`}
                            title={pdfPreview.fileName}
                            className="h-full w-full bg-white"
                        />
                    )}
                </div>
            </Modal>
            {renderStepIndicator()}

            <div className="min-h-[420px]">
                {renderCurrentStep()}
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex gap-3">
                    <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
                        取消
                    </Button>
                    <Button variant="outline" onClick={handleSaveDraft} disabled={isSubmitting}>
                        <Save className="mr-2 h-4 w-4" />
                        保存草稿
                    </Button>
                </div>

                <div className="flex gap-3 sm:justify-end">
                    {currentStep > 1 && (
                        <Button variant="outline" onClick={goPrev} disabled={isSubmitting}>
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            上一步
                        </Button>
                    )}
                    {currentStep < stepLabels.length ? (
                        <Button variant="primary" onClick={goNext} disabled={isSubmitting || uploading}>
                            下一步
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    ) : (
                        <Button variant="primary" onClick={handleFinalSubmit} disabled={isSubmitting}>
                            {isSubmitting ? '处理中...' : (
                                <>
                                    <CheckCircle className="mr-2 h-5 w-5" />
                                    提交合同
                                </>
                            )}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
